import { List } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { Link } from 'react-router';

import { GlobalModals, OrderSummaryContexts, TransitModes } from '../../constants/AppConstants';
import config from '../../config';
import Order from '../../records/Order';
import { translate as t } from '../../lib/I18n';
import { sortSplitGroupTripsForDisplay } from '../../utils/splits';

import FeatureFlagStore from '../../stores/FeatureFlagStore';
import Trip from './Trip';
import RoversRangersTrip from '../roversRangers/checkout/Trip';
import SeasonTrip from './SeasonTrip';
import FareOnlyTrip from './FareOnlyTrip';
import EmptyBasket from './EmptyBasket';
import PaymentInformation from './orderSummary/PaymentInformation';
import RefundInformation from './orderSummary/RefundInformation';
import Totals from './orderSummary/Totals';
import DeliveryInformation from './orderSummary/DeliveryInformation';
import SovendusBanner from './orderSummary/SovendusBanner';
import NRETransactionFrame from '../nre/TransactionFrame';
import FeatureFlag from '../general/FeatureFlag';
import ExternalLink from '../general/ExternalLink';
import RewardPointsInformation from '../account/orders/RewardPointsInformation';
import TrackingActions from '../../actions/TrackingActions';
import ModalActions from '../../actions/ModalActions';
import Changeovers from './orderSummary/Changeovers';


class OrderSummary extends React.PureComponent {

    static propTypes = {
        order: PropTypes.instanceOf(Order).isRequired,
        refundDetails: PropTypes.instanceOf(Order),
        storedRefundCalculations: PropTypes.instanceOf(List),
        showHeader: PropTypes.bool,
        isLocked: PropTypes.bool,
        isLoggedIn: PropTypes.bool,
        isAccountOrderDetails: PropTypes.bool,
        isInApp: PropTypes.bool,
        adminFee: PropTypes.number,
        context: PropTypes.oneOf(_.values(OrderSummaryContexts)),
        timetableAlerts: PropTypes.array,
        setBasketChanged: PropTypes.func,
        pendingTravelAssistRequests: PropTypes.array,
    };

    static defaultProps = {
        storedRefundCalculations: new List(),
        showHeader: true,
        isLocked: false,
        isLoggedIn: false,
        isAccountOrderDetails: false,
        isInApp: false,
        context: null,
        timetableAlerts: [],
        setBasketChanged: _.noop,
        pendingTravelAssistRequests: [],
    };

    brand = config.get('brand');

    _goBackToJourneyPlanner () {
        TrackingActions.addAnotherJourneyOnBasketModal("add-another-journey");
        ModalActions.close(GlobalModals.BASKET);
    }

    _getTripMatches (orderTrip, groupTickets = null) {
        const { timetableAlerts, pendingTravelAssistRequests } = this.props;

        let matchingTimetableAlert = null;
        let hasMatchingPAJourney = false;

        const { outwardSingle, return: returnFare } = orderTrip.fares;

        let orderJourney = null;

        if (outwardSingle) {
            orderJourney = outwardSingle.outwardJourney;
        } else if (returnFare) {
            orderJourney = returnFare.outwardJourney;
        }


        const orderJourneyLegs = orderJourney && orderJourney.legs;

        if (FeatureFlagStore.get('enableTimetableAlertsWidget') && orderJourney) {
            const trainLegs = orderJourneyLegs.filter(l => l.mode === TransitModes.TRAIN);
            const sortedLegs = trainLegs.map(l => l.tuid).toJS().sort();

            // if all train UIDs on the orderJourney match the train UIDs from the JP journey, match
            matchingTimetableAlert = timetableAlerts.find(timetableAlert => {
                const outwardTrainIds = timetableAlert.get('outwardTrainIds') && timetableAlert.get('outwardTrainIds').toJS();
                const inwardTrainIds = timetableAlert.get('inwardTrainIds') && timetableAlert.get('inwardTrainIds').toJS();

                // Match by train IDs (widget-state alerts from checkout)
                if (outwardTrainIds || inwardTrainIds) {
                    return (
                        _.isEqual(outwardTrainIds?.sort(), sortedLegs) ||
                        _.isEqual(inwardTrainIds?.sort(), sortedLegs)
                    );
                }

                // Match by ticketId (API-loaded ServiceChangeAlert records on order details page)
                const alertTicketId = timetableAlert.get('ticketId');
                if (alertTicketId) {
                    const tripTicketIds = (groupTickets || orderTrip.getTickets()).map(ticket => ticket.id).toJS();
                    return tripTicketIds.includes(alertTicketId);
                }

                return false;
            });
        }

        if (FeatureFlagStore.get('enablePassengerAssist') && orderJourney) {
            let matchingRequest = null;

            if (pendingTravelAssistRequests) {
                matchingRequest = pendingTravelAssistRequests.find(req => req.orderTripId === orderTrip.id);
            }

            const {
                outwardEnabled,
                returnEnabled,
            } = matchingRequest || {};

            if (outwardSingle) {
                hasMatchingPAJourney = outwardEnabled;
            } else if (returnFare) {
                hasMatchingPAJourney = returnEnabled;
            }
        }

        return { matchingTimetableAlert, hasMatchingPAJourney };
    }

    _getSharedTripProps () {
        const {
            order,
            isLocked,
            isLoggedIn,
            refundDetails,
            storedRefundCalculations,
            context,
            setBasketChanged,
        } = this.props;

        return {
            order,
            refundDetails,
            isLocked,
            isLoggedIn,
            open: order.isPaid(),
            storedRefundCalculations,
            context,
            setBasketChanged,
        };
    }

    _renderGroupedTrips () {
        const { order } = this.props;

        const groups = order.getGroupedRegularAndSplitItemsList().map(wrapper =>
            wrapper.regular ? [wrapper.regular] : sortSplitGroupTripsForDisplay(wrapper.split.items)
        );

        const oldFareCount = groupTrips =>
            order.getOldFaresForFares(new List(groupTrips).flatMap(trip => trip.getJourneyFares())).size;

        //sort so amended journeys (with a matching old fare) render first, as on the ungrouped path
        const ordered = [...groups].sort((a, b) => oldFareCount(b) - oldFareCount(a));

        return ordered.map((groupTrips, index) => {
            const [orderTrip, ...siblingTrips] = groupTrips;
            const groupTickets = siblingTrips.length
                ? new List(groupTrips).flatMap(trip => trip.getTickets())
                : null;
            const { matchingTimetableAlert, hasMatchingPAJourney } = this._getTripMatches(orderTrip, groupTickets);

            const sharedProps = {
                ...this._getSharedTripProps(),
                key: index,
                index,
                orderTrip,
                additionalOrderTrips: new List(siblingTrips),
                hasMatchingPAJourney,
            };

            if (orderTrip.hasSeasonFare()) { return <SeasonTrip { ...sharedProps } />; }
            if (orderTrip.hasRoversRangersFare()) { return <RoversRangersTrip { ...sharedProps } />; }
            if (orderTrip.hasFareOnlyFare()) { return <FareOnlyTrip { ...sharedProps } />; }

            return <Trip { ...sharedProps } matchingTimetableAlert={ matchingTimetableAlert } />;
        });
    }

    _renderTrips () {
        const { order } = this.props;

        if (!order.hasChangeoverTickets() && order.hasSplitItems()) {
            return this._renderGroupedTrips();
        }

        const trips = order.hasChangeoverTickets() ? order.getChangeoverTrips() : order.trips;

        //sort so amended trips (with a matching old fare) render first
        const sortedTrips = trips.sortBy(trip =>
            order.getOldFaresForFares(trip.getJourneyFares()).size
        ).reverse();

        return sortedTrips.map((orderTrip, index) => {
            const { matchingTimetableAlert, hasMatchingPAJourney } = this._getTripMatches(orderTrip);

            const sharedProps = {
                ...this._getSharedTripProps(),
                key: index,
                index,
                orderTrip,
                hasMatchingPAJourney,
            };

            if (orderTrip.hasSeasonFare()) { return <SeasonTrip { ...sharedProps } />; }
            if (orderTrip.hasRoversRangersFare()) { return <RoversRangersTrip { ...sharedProps } />; }
            if (orderTrip.hasFareOnlyFare()) { return <FareOnlyTrip { ...sharedProps } />; }

            return <Trip { ...sharedProps } matchingTimetableAlert={ matchingTimetableAlert } />;
        });
    }

    _renderDelayRepayInfo () {
        return (
            <FeatureFlag name="enableBookingConfirmationDelayRepayInfo">
                <div className="box--light box--rounded bg--white margin-bottom--sm margin-top--sm">
                    <div className="order-summary__delay-repay-info margin-top--sm margin-bottom--sm text-weight--normal">
                        {this.brand === 'southeastern' && <p>
                            { t('confirmation.delayRepayInfo.сarbonFootprintCalculator') }
                            <ExternalLink
                                message={ t('confirmation.delayRepayInfo.сarbonFootprintCalculator.link') }
                                href={ `https://www.southeasternrailway.co.uk/environment` }
                            />
                        </p>}
                        <h3 className="text-primary text-weight--normal">{ t('confirmation.delayRepayInfo.title') }</h3>
                        <p>{ t('confirmation.delayRepayInfo.message') }</p>
                        <p>
                            <ExternalLink
                                message={ t('confirmation.delayRepayInfo.link') }
                                href={ config.get('delayRepayBrochureUrl') ?? `https://${config.get('delayRepayHost')}/` }
                            />
                        </p>
                    </div>
                </div>
            </FeatureFlag>
        );
    }

    render () {
        const { order, refundDetails, showHeader, isLoggedIn, isInApp, isAccountOrderDetails, adminFee, storedRefundCalculations, context } = this.props;
        const isFullRefund = _.get(refundDetails, 'isFullRefund');

        if (order.isEmpty()) {
            return <EmptyBasket />;
        }

        return (
            <section>
                {refundDetails && order.hasRefunds() &&
                    <div className="box-headed__body box--padded margin-bottom--sm">
                        {t(`account.orders.${refundDetails.isFullRefund ? 'refunded' : 'partialRefund'}`)}
                    </div>
                }

                <div className="box--light box--rounded bg--white margin-bottom--sm margin-top">
                    { showHeader ?
                        <div className="margin-bottom--sm hidden-print">
                            <h2 className="header--h1 border--bottom padding-bottom--sm text-center">{ t('order.summary.heading') }</h2>
                        </div> : "" }

                    <div className="order-summary__total hidden-print">
                        {!isFullRefund &&
                            <DeliveryInformation order={ order } isLoggedIn={ isLoggedIn } />
                        }
                        <PaymentInformation order={ order } />
                        <RefundInformation isFullRefund={ isFullRefund } refund={ refundDetails } />
                    </div>

                    <Totals
                        order={ order }
                        refund={ refundDetails }
                        adminFee={ adminFee }
                    />
                </div>

                <FeatureFlag name="enableRewardPoints">
                    {context !== OrderSummaryContexts.PAYMENT_CONFIRMATION && context !== OrderSummaryContexts.BASKET_MODAL &&
                        <div className="box--light box--rounded bg--white margin-bottom--sm margin-top padding--horiz-md padding--vert-sm">
                            <RewardPointsInformation paidOrder={order}/>
                        </div>
                    }
                </FeatureFlag>

                { context === OrderSummaryContexts.PAYMENT_CONFIRMATION && FeatureFlagStore.get('enableCheckoutPromotion') &&
                    <SovendusBanner order={ order } />
                }

                <ul className="order-summary__list list-unstyled">
                    { this._renderTrips() }
                </ul>

                { order.isSTicket() && isAccountOrderDetails ? (
                    <Changeovers order={ order } />
                ) : null }

                <div className="box--light box--rounded bg--white margin-bottom--sm margin-top--sm">
                    <Totals
                        order={ order }
                        refund={ refundDetails }
                        adminFee={ adminFee }
                        storedRefundCalculations={ storedRefundCalculations }
                    />
                </div>

                <FeatureFlag name="enableNREHandoffLogging">
                    <NRETransactionFrame order={ order }/>
                </FeatureFlag>

                { order.isPaid() && this._renderDelayRepayInfo() }

                { !isInApp ? (
                <div className="order-summary__button-container">
                    <Link to="/search" tabIndex="-1">
                        <button
                            onClick={this._goBackToJourneyPlanner}
                            className="action-btn action-btn--secondary-bold--icon action-btn--secondary-bold"
                        >
                            <i className="otrl otrl-add" />
                            { t('button.text.addjourney') }
                        </button>
                    </Link>
                </div>
                ) : null }
            </section>
        );
    }
}

export default OrderSummary;
